import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { CheckCircle2, FileText, Download, AlertCircle } from "lucide-react";

const DOCUMENT_LABELS = {
  aadhaar: "Aadhaar Card",
  pan: "PAN Card",
  bank: "Bank Passbook / Cheque",
  education: "Education Certificate",
  experience: "Experience Letter",
  photo: "Passport Photo",
  address_proof: "Address Proof",
  custom: "Other / Custom",
};

function humanSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function PortalDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/my-portal/documents");
        setDocs(data || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="portal-documents">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">My Documents</div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          KYC & Other Documents
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Documents uploaded by HR on your behalf. To add or update, please reach out to your HR.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading...</div>
      ) : docs.length === 0 ? (
        <div className="bg-white rounded-xl border border-red-100 p-8 text-center">
          <AlertCircle className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <div className="text-sm text-slate-600 font-medium">No documents on file</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {docs.map((d) => (
            <div
              key={d.id}
              data-testid={`mydoc-${d.id}`}
              className="bg-white rounded-xl border border-red-100 p-4 flex flex-col gap-2"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 rounded-md bg-red-50 flex items-center justify-center brand-text">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-slate-900 truncate">
                      {DOCUMENT_LABELS[d.document_type] || d.document_type}
                    </div>
                    {d.custom_name && (
                      <div className="text-xs text-slate-500 truncate">{d.custom_name}</div>
                    )}
                  </div>
                </div>
                {d.verified ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                    <CheckCircle2 className="h-3 w-3" /> Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                    Pending
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 truncate" title={d.file_name}>
                {d.file_name} · {humanSize(d.file_size)}
              </div>
              <div className="text-[11px] text-slate-400">
                Uploaded {d.uploaded_at ? formatDate(d.uploaded_at) : "—"}
              </div>
              <a
                href={`/api/files/${d.file_url}`}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium"
                data-testid={`mydoc-view-${d.id}`}
              >
                <Download className="h-3.5 w-3.5" /> View / Download
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
